/**
 * Google Analytics 4 Integration
 *
 * Provides centralized analytics tracking for a WebSocket-based SPA
 */

// Type declarations for gtag
declare global {
    interface Window {
        gtag?: (
            command: 'config' | 'event' | 'set',
            targetId: string,
            config?: Record<string, any>
        ) => void;
        dataLayer?: any[];
    }
}

// Check if GA4 is enabled via environment variable
const GA_MEASUREMENT_ID = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;
const isAnalyticsEnabled = !!GA_MEASUREMENT_ID;

/**
 * Initialize Google Analytics
 * Call this once when the app loads
 */
export const initAnalytics = (): void => {
    if (!isAnalyticsEnabled) {
        console.log('Google Analytics disabled - no measurement ID provided');
        return;
    }

    if (!window.gtag) {
        console.warn('Google Analytics not loaded - check if script is in index.html');
        return;
    }

    // Configure GA4
    window.gtag('config', GA_MEASUREMENT_ID!, {
        send_page_view: false, // We'll manually track virtual pageviews
        anonymize_ip: true, // Privacy-friendly
    });

    console.log('Google Analytics initialized:', GA_MEASUREMENT_ID);
};

/**
 * Track a virtual pageview (for view changes without URL changes)
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
    if (!isAnalyticsEnabled || !window.gtag) return;

    window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle || pagePath,
    });
};

/**
 * Track a custom event
 */
export const trackEvent = (
    eventName: string,
    eventParams?: Record<string, any>
): void => {
    if (!isAnalyticsEnabled || !window.gtag) return;

    // Filter out sensitive data
    const sanitizedParams = sanitizeEventParams(eventParams);

    window.gtag('event', eventName, sanitizedParams);
};

/**
 * Set user properties (persistent across events)
 */
export const setUserProperties = (properties: Record<string, any>): void => {
    if (!isAnalyticsEnabled || !window.gtag) return;

    window.gtag('set', 'user_properties', properties);
};

/**
 * Sanitize event parameters to remove sensitive data
 */
const sanitizeEventParams = (
    params?: Record<string, any>
): Record<string, any> => {
    if (!params) return {};

    const sanitized: Record<string, any> = {};

    for (const [key, value] of Object.entries(params)) {
        // Skip sensitive fields
        if (
            key.toLowerCase().includes('password') ||
            key.toLowerCase().includes('token') ||
            key.toLowerCase().includes('secret') ||
            key.toLowerCase().includes('key')
        ) {
            continue;
        }

        if (typeof value === 'object' && value !== null) {
            // Don't send complex objects
            sanitized[key] = '[object]';
        } else {
            sanitized[key] = value;
        }
    }

    return sanitized;
};

/**
 * Analytics event names for common socket events
 */
export const AnalyticsEvents = {
    // Authentication
    LOGIN: 'login',
    LOGOUT: 'logout',
    SIGNUP: 'signup',

    // Project lifecycle
    PROJECT_CREATE: 'project_create',
    PROJECT_OPEN: 'project_open',
    PROJECT_SAVE: 'project_save',
    PROJECT_DELETE: 'project_delete',
    PROJECT_RENAME: 'project_rename',

    // Engine
    ENGINE_START: 'engine_start',
    ENGINE_STOP: 'engine_stop',

    // Nodes
    NODE_ADD: 'node_add',
    NODE_DELETE: 'node_delete',
    NODE_CONNECT: 'node_connect',
    NODE_DISCONNECT: 'node_disconnect',

    // Files
    FILE_UPLOAD: 'file_upload',
    FILE_DOWNLOAD: 'file_download',
    FILE_DELETE: 'file_delete',

    // Templates
    TEMPLATE_LOAD: 'template_load',

    // Errors
    ERROR: 'error_occurred',
} as const;

/**
 * Track authentication events
 */
export const trackAuth = (
    action: 'login' | 'logout' | 'signup',
    username?: string,
    success: boolean = true
): void => {
    trackEvent(action, {
        success,
    });

    if (action === 'login' && success && username) {
        setUserProperties({
            username: username,
        });
    }
};

/**
 * Track project-related events
 */
export const trackProject = (
    action: 'create' | 'open' | 'save' | 'delete' | 'rename',
    projectName?: string
): void => {
    trackEvent(`project_${action}`, {
        project_name: projectName,
    });
};

/**
 * Track engine events
 */
export const trackEngine = (action: 'start' | 'stop'): void => {
    trackEvent(`engine_${action}`);
};

/**
 * Track node operations
 */
export const trackNode = (
    action: 'add' | 'delete' | 'connect' | 'disconnect',
    nodeType?: string
): void => {
    trackEvent(`node_${action}`, {
        node_type: nodeType,
    });
};

/**
 * Track errors
 */
export const trackError = (errorType: string, errorMessage?: string): void => {
    trackEvent(AnalyticsEvents.ERROR, {
        error_type: errorType,
        error_message: errorMessage?.substring(0, 100), // Limit length
    });
};

/**
 * Track user type (guest vs authenticated)
 */
export const trackUserType = (isGuest: boolean, isAuthenticated: boolean): void => {
    setUserProperties({
        user_type: isGuest ? 'guest' : 'authenticated',
        is_authenticated: isAuthenticated,
    });
};

/**
 * Track template usage
 */
export const trackTemplate = (templateName?: string): void => {
    trackEvent(AnalyticsEvents.TEMPLATE_LOAD, {
        template_name: templateName,
    });
};
